import { useSyncExternalStore } from 'react';
import { Bot } from '../../../icons';
import type { ChatMessageEvent, AiActionType } from '../../../types/chat';
import { AiManualCard } from './AiManualCard';
import { MeetingSummaryCard } from './MeetingSummaryCard';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';

interface AiActionCardProps {
  message: ChatMessageEvent;
  /** Phase 5 ai-gateway 도착 시 이 prop 으로 sub-card 분기. 미지정/미상이면 generic placeholder. */
  actionType?: AiActionType;
}

/**
 * AiActionCard (P2-16e) — AI 액션 카드 일반 컨테이너 + sub-card 라우터.
 *
 * 시안 line 1190 `{msg.isCard && <VacationCard />}` 의 일반화. 라우팅 우선순위:
 *
 *   1) `aiCardType === 'manual'` 또는 `isManualCard === true` (legacy 필드 호환)
 *      → AiManualCard (P2-16h, 시안 ManualCard)
 *   2) `actionType` prop 별 sub-card (G4 6값 + unknown — adr-20260429-ai-action-type-naming):
 *      - leave_request    → LeaveRequestCard    (P2-16f)
 *      - meeting_summary  → MeetingSummaryCard  (P2-16g)
 *      - business_trip    → BusinessTripCard    (P2-16g)
 *      - overtime_request → OvertimeCard        (P2-16g)
 *      - manual_lookup    → AiManualCard        (1 과 동일 — legacy actionType 경로)
 *      - schedule_query   → ScheduleQueryHint   (G4 — 별도 카드 없이 AiTextMessage 인라인 응답
 *                                                정책. 본 hint 는 ai-gateway 가 ai_card_type=null
 *                                                + ai_action_id 만 발화한 misuse 시 안내).
 *      - unknown / 미지정  → UnknownActionPlaceholder
 *
 * **ADR — aiCardType vs isManualCard 우선순위**: backend SSOT 단일화까지 두 필드 OR 결합.
 * 둘 다 진실인 모순 케이스는 manual 우선 (SR 사용자에게 매뉴얼 안내가 더 안전).
 * Phase 5 ai-gateway 합류 시 `aiCardType` 단일 SSOT 로 통일하고 isManualCard deprecated.
 *
 * 각 sub-card 는 자체적으로 aiActionData 부재 / payload kind mismatch 시 fallback 처리
 * (pattern-forward-declared-optional-payload-graceful-render).
 */
export function AiActionCard({ message, actionType = 'unknown' }: AiActionCardProps) {
  if (message.aiCardType === 'manual' || message.isManualCard) {
    return <AiManualCard message={message} />;
  }

  switch (actionType) {
    // V2.6.1 — leave_request / business_trip / overtime_request HR 도메인 cards 제거
    case 'meeting_summary':
      return <MeetingSummaryCard message={message} />;
    case 'manual_lookup':
      return <AiManualCard message={message} />;
    case 'schedule_query':
      return <ScheduleQueryHint message={message} />;
    default:
      return <UnknownActionPlaceholder message={message} />;
  }
}

/**
 * G4 — schedule_query 는 별도 결재 카드 없이 AiTextMessage 인라인 응답 정책 (ADR §2).
 * 본 컴포넌트는 ai-gateway 가 잘못 ai_card_type='action' + action_type='schedule_query' 로
 * 발화한 misuse 시의 graceful fallback — 사용자에게 위 메시지를 참고하라고 안내.
 */
function ScheduleQueryHint({ message }: { message: ChatMessageEvent }) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  return (
    <div className="chat-ai-action-card chat-ai-action-card--schedule-query" role="region" aria-label={tChat('chat.ai_action.schedule.aria')}>
      <div className="chat-ai-action-card-header">
        <div className="chat-ai-action-card-icon">
          <Bot size={16} />
        </div>
        <div className="chat-ai-action-card-title-block">
          <div className="chat-ai-action-card-title">{tChat('chat.ai_action.schedule.title')}</div>
          <div className="chat-ai-action-card-subtitle">{tChat('chat.ai_action.schedule.subtitle')}</div>
        </div>
      </div>
      <div className="chat-ai-action-card-body">
        <div className="chat-ai-action-card-content">{message.content}</div>
      </div>
    </div>
  );
}

function UnknownActionPlaceholder({ message }: { message: ChatMessageEvent }) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  return (
    <div className="chat-ai-action-card chat-ai-action-card--unknown" role="region" aria-label={tChat('chat.ai_action.unknown.aria')}>
      <div className="chat-ai-action-card-header">
        <div className="chat-ai-action-card-icon">
          <Bot size={16} />
        </div>
        <div className="chat-ai-action-card-title-block">
          <div className="chat-ai-action-card-title">{tChat('chat.ai_action.unknown.title')}</div>
          <div className="chat-ai-action-card-subtitle">{tChat('chat.ai_action.unknown.subtitle')}</div>
        </div>
      </div>
      <div className="chat-ai-action-card-body">
        <div className="chat-ai-action-card-content">{message.content}</div>
      </div>
    </div>
  );
}
